/**
 * ProfileTable Component
 *
 * Displays a paginated table of permission profiles with:
 * - Loading state
 * - Refreshing overlay (after create/update/delete)
 * - Row actions (edit on double-click, delete)
 * - Pagination
 *
 * NOTE: This is a sample implementation. Merge the isRefreshing overlay
 * into your existing ProfileTable component.
 */

import React from 'react';
import { Loader2, Trash2, ChevronLeft, ChevronRight, Shield } from 'lucide-react';

const ProfileTable = ({
                          profiles,
                          isLoading,
                          isRefreshing,
                          pagination,
                          onRowDoubleClick,
                          onDeleteClick,
                          onPageChange,
                      }) => {
    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden relative">
            {/* Refreshing overlay - shows after create/update/delete while data reloads */}
            {isRefreshing && (
                <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-xl">
                    <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                        <span className="text-sm text-gray-600 font-medium">Updating...</span>
                    </div>
                </div>
            )}

            <table className="w-full">
                <thead>
                <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Profile Name
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Description
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Users
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                        Permissions
                    </th>
                    <th className="px-5 py-3 w-10"></th>
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                    <tr>
                        <td colSpan={5} className="px-5 py-12 text-center">
                            <Loader2 className="animate-spin mx-auto text-gray-400" size={24} />
                            <p className="mt-2 text-sm text-gray-500">Loading profiles...</p>
                        </td>
                    </tr>
                ) : profiles.length === 0 ? (
                    <tr>
                        <td colSpan={5} className="px-5 py-12 text-center">
                            <Shield className="mx-auto text-gray-300" size={40} />
                            <p className="mt-2 text-sm text-gray-500">No profiles found</p>
                        </td>
                    </tr>
                ) : (
                    profiles.map((profile) => (
                        <tr
                            key={profile.uuid}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onDoubleClick={() => onRowDoubleClick(profile)}
                        >
                            <td className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center">
                                        <Shield className="text-purple-600" size={16} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{profile.name}</p>
                                        {profile.isSystem && (
                                            <span className="text-xs text-gray-400">System</span>
                                        )}
                                    </div>
                                </div>
                            </td>
                            <td className="px-5 py-4 hidden sm:table-cell">
                                <p className="text-sm text-gray-600 truncate max-w-xs">
                                    {profile.description || '—'}
                                </p>
                            </td>
                            <td className="px-5 py-4 hidden md:table-cell">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700">
                                        {profile.userCount || 0} users
                                    </span>
                            </td>
                            <td className="px-5 py-4 hidden lg:table-cell">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700">
                                        {profile.permissionCount || 0} permissions
                                    </span>
                            </td>
                            <td className="px-5 py-4">
                                {!profile.isSystem && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteClick(profile);
                                        }}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete profile"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))
                )}
                </tbody>
            </table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                        Showing {pagination.page * pagination.size + 1} to{' '}
                        {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of{' '}
                        {pagination.totalElements} profiles
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onPageChange(pagination.page - 1)}
                            disabled={!pagination.hasPrevious}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {pagination.page + 1} of {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => onPageChange(pagination.page + 1)}
                            disabled={!pagination.hasNext}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileTable;